import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useState } from "react";

import { hostname } from "../../../utils";

interface GenerateWidgetGifDialogProps {
  open: boolean;
  onClose: () => void;
  agentId: string;
  companyId: string;
  avatarImage: string;
  getToken: () => Promise<string>;
  onGenerated: (videoUrl: string) => void;
}

const CREDIT_COST = 30;
const DEFAULT_PROMPT = "Person waving like a nice salesperson";

const GenerateWidgetGifDialog: React.FC<GenerateWidgetGifDialogProps> = ({
  open,
  onClose,
  agentId,
  companyId,
  avatarImage,
  getToken,
  onGenerated,
}) => {
  const [prompt, setPrompt] = useState(DEFAULT_PROMPT);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!avatarImage) {
      setError("Please set an avatar image for the agent first");
      return;
    }

    setGenerating(true);
    setError(null);
    setGeneratedUrl(null);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/agents/${agentId}/generate-widget-gif`,
        {
          companyId,
          prompt,
          imageUrl: avatarImage,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const videoUrl = response.data.videoUrl;
      setGeneratedUrl(videoUrl);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || "Failed to generate widget animation";
      setError(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  const handleUseVideo = () => {
    if (generatedUrl) {
      onGenerated(generatedUrl);
      handleClose();
    }
  };

  const handleClose = () => {
    setPrompt(DEFAULT_PROMPT);
    setError(null);
    setGeneratedUrl(null);
    setGenerating(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={generating ? undefined : handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Generate Widget Animation
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={generating}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Generate an animated video from your agent's avatar image. The
          animation will be used as the widget button on your website.
        </Typography>

        {/* Avatar Preview */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Source Image
          </Typography>
          {avatarImage ? (
            <Avatar
              src={avatarImage}
              sx={{
                width: 80,
                height: 80,
                border: 2,
                borderColor: "divider",
              }}
            />
          ) : (
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: "grey.200",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                No Avatar
              </Typography>
            </Box>
          )}
        </Box>

        {/* Prompt Input */}
        <TextField
          label="Animation Prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          fullWidth
          multiline
          rows={2}
          disabled={generating}
          helperText="Describe how you want the person in the image to animate"
          sx={{ mb: 2 }}
        />

        {/* Credit Cost Notice */}
        <Alert severity="info" sx={{ mb: 2 }}>
          This will cost <strong>{CREDIT_COST} credits</strong>. The animation
          takes about 1-2 minutes to generate.
        </Alert>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Generating State */}
        {generating && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 3,
            }}
          >
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Generating animation... This may take 1-2 minutes.
            </Typography>
          </Box>
        )}

        {/* Generated Video Preview */}
        {generatedUrl && !generating && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Generated Animation Preview
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  overflow: "hidden",
                  boxShadow: 3,
                }}
              >
                <video
                  src={generatedUrl}
                  autoPlay
                  loop
                  muted
                  playsInline
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>
            </Box>
            <Alert severity="success">
              Animation generated successfully! Click "Use This Animation" to
              apply it to your widget.
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={generating}>
          Cancel
        </Button>
        {generatedUrl ? (
          <Button variant="contained" onClick={handleUseVideo}>
            Use This Animation
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={generating || !avatarImage}
            startIcon={
              generating ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AutoAwesomeIcon />
              )
            }
          >
            {generating ? "Generating..." : `Generate (${CREDIT_COST} credits)`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GenerateWidgetGifDialog;
